// A trainer's day drawn against the clock, the way a planner page draws one.
//
// The list this replaced showed teaching slots and nothing else, so a day could
// read as free at eight in the morning while the person was on morning feed
// until ten. Availability answers "when can they teach"; it never answered
// "what else are they doing". Both are hours out of the same day, and a person
// planning one has to see the other.
//
// Hours down the left, entries against them. A shift that runs eight to three
// is ONE block spanning those hours, not seven rows repeating its name — the
// hours are already on the rail, and saying "Barn chores" seven times fills the
// page without adding a fact.
//
// Shifts run in a narrow column of their own, beside the day rather than in it.
// They are the reason an afternoon is spoken for; the lessons are the afternoon.
// Sharing one set of lanes made a seven-hour shift claim the left column all
// morning, so lessons inside it sat right and lessons after it snapped back
// left — the page re-flowing under a reader for whom nothing had changed.
//
// What can be touched, and what can't:
//
//   open slot   the pencil, exactly as before. An unbooked hour is the one
//               thing on this page that is still a decision.
//   booked      "Change" — the shape of the lesson, for that date only. Not
//               the booking: a client who is coming stays coming, and undoing
//               that by tapping a calendar is not a gesture worth having.
//               Making their private into a semi-private is a different act
//               and a real one, and it was impossible until slot overrides.
//   shift       read-only. It belongs to the roster, where whoever owns it can
//               see the week it sits in.
//
// When client booking arrives it reads the same entries and shows only the open
// ones, which is why "open" is a kind here rather than a styling accident.

import { dayPlan } from '../../lib/dayTimeline';
import { axisTicks, tickLabel } from '../../lib/shifts';
import { colorClass } from '../../lib/lessonTypes';
import { IconButton, PencilIcon } from '../ui/icons';

// Tall enough that a 30-minute slot still has a line of text in it. Below about
// fifty the short entries collapse into stripes you have to hover to read.
const PX_PER_HOUR = 56;

function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function DayTimeline({
  shifts = [],
  timeOff = [],
  slots = [],
  // An absence with no hours on it. It has nothing to draw against, so it
  // washes the whole column instead of taking a lane and burying the rest.
  allDayOff = null,
  onBlockSlot,
  onUnblock,
  // Null unless this reader may change the shape of a booked lesson.
  onConvert = null,
  busy = false,
}) {
  const { entries, axis, contextLanes, dayLanes } = dayPlan({ shifts, timeOff, slots });
  if (entries.length === 0 && !allDayOff) return null;

  // The gutter exists only when something is in it. A third of the width
  // reserved for nothing makes every lesson narrower for no reason.
  const gutter = contextLanes ? 30 : 0;

  const height = ((axis.end - axis.start) / 60) * PX_PER_HOUR;
  const ticks = axisTicks(axis);

  return (
    <div className="rounded-md border border-ink-200 bg-white overflow-hidden">
      {allDayOff && (
        <p className="px-3 py-1.5 text-xs font-medium text-ink-500 bg-ink-100 border-b border-ink-200">
          {allDayOff.reason || 'Away'} · all day
        </p>
      )}

      {/* The padding is load-bearing: every hour label is centred on its line,
          so the first one hangs half a line above the top edge and was being
          clipped to a row of half-letters. */}
      <div className="flex py-2" style={{ height: `${height + 16}px` }}>
        {/* The hour rail. Labels sit ON the line rather than between the lines,
            so an entry starting at nine lines up with the word "9 AM". */}
        <div className="relative w-14 shrink-0 border-r border-ink-100">
          {ticks.map((m) => (
            <span
              key={m}
              className="absolute right-1.5 -translate-y-1/2 text-[10px] tabular-nums text-ink-400"
              style={{ top: `${((m - axis.start) / (axis.end - axis.start)) * 100}%` }}
            >
              {/* The last tick is the axis end; labelling it would print a time
                  nothing reaches, hanging off the bottom edge. */}
              {m === axis.end ? '' : tickLabel(m)}
            </span>
          ))}
        </div>

        <div className="relative flex-1 min-w-0">
          {/* Hour lines, the ruled feint of the page. */}
          {ticks.map((m) => (
            <span
              key={m}
              aria-hidden
              className="absolute left-0 right-0 border-t border-ink-100"
              style={{ top: `${((m - axis.start) / (axis.end - axis.start)) * 100}%` }}
            />
          ))}

          {allDayOff && <span aria-hidden className="absolute inset-0 bg-ink-100/60" />}

          {entries.map((e) => (
            <Entry
              key={e.id}
              entry={e}
              gutter={gutter}
              lanes={e.group === 'context' ? contextLanes : dayLanes}
              onBlockSlot={onBlockSlot}
              onUnblock={onUnblock}
              onConvert={onConvert}
              busy={busy}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Entry({ entry: e, gutter, lanes, onBlockSlot, onUnblock, onConvert, busy }) {
  const context = e.group === 'context';
  const band = context ? gutter : 100 - gutter;
  const width = band / Math.max(1, lanes);
  const style = {
    top: `${e.top}%`,
    height: `${e.height}%`,
    left: `${(context ? 0 : gutter) + e.lane * width}%`,
    width: `calc(${width}% - 3px)`,
  };

  const skin =
    e.kind === 'shift'
      ? colorClass(e.color, 'card')
      : e.kind === 'off'
        // Grey, and the same block as everything else. The point of drawing it
        // here rather than in a line of prose above is that an afternoon off
        // should LOOK like an occupied afternoon.
        ? 'bg-ink-100 border-ink-300 text-ink-500'
        : e.kind === 'booked'
          // Green, and stronger than it wants to be: at a tenth it sat within a
          // few points of the grey used for time off, and "somebody is coming"
          // and "nobody is here" are the two states that must never be
          // confusable at a glance.
          ? 'bg-hunter/15 border-hunter/50 text-ink-900'
          // A plain hairline, not a dash. Three unbooked hours in a row drew
          // three dashed boxes and the panel read as a wireframe; the block now
          // says "Open" in words, which is where that fact belongs.
          : 'bg-white border-ink-200 text-ink-500';

  const s = e.source;

  return (
    <div
      className={`absolute rounded border px-1.5 py-0.5 overflow-hidden text-[11px] leading-tight ${skin}`}
      style={style}
    >
      <div className="flex items-baseline gap-1">
        <span className="tabular-nums shrink-0 opacity-70">{fmtTime(e.startTime)}</span>
        <span className="font-medium truncate">{e.title}</span>
        {e.kind === 'open' && s.capacity > 1 && (
          <span className="shrink-0 opacity-70">· {s.capacity} places</span>
        )}

        {e.kind === 'open' && onBlockSlot && (
          <span className="ml-auto shrink-0 -my-0.5">
            <IconButton
              icon={PencilIcon}
              label={`Change ${fmtTime(e.startTime)}`}
              onClick={() => onBlockSlot(s)}
              disabled={busy}
            />
          </span>
        )}

        {e.kind === 'booked' && onConvert && (
          <button
            type="button"
            onClick={() => onConvert(s)}
            disabled={busy}
            className="ml-auto shrink-0 text-hunter/80 hover:text-hunter underline underline-offset-2"
          >
            Change
          </button>
        )}

        {e.kind === 'off' && onUnblock && (
          <button
            type="button"
            onClick={() => onUnblock(s)}
            disabled={busy}
            className="ml-auto shrink-0 text-ink-400 hover:text-ink-700 underline underline-offset-2"
          >
            put it back
          </button>
        )}
      </div>

      {e.kind === 'booked' && (
        <div className="truncate">
          {s.riders.map((r) => r.name).join(', ')}
          {s.riders[0]?.horseName && (
            <span className="text-ink-400"> · {s.riders[0].horseName}</span>
          )}
          {s.open > 0 && <span className="text-ink-400"> · {s.open} still open</span>}
        </div>
      )}

      {e.kind === 'shift' && e.detail && <div className="truncate opacity-80">{e.detail}</div>}

      {/* Marked, because a one-off semi-private that looks identical to a
          recurring one is a thing somebody will "tidy up" by editing the
          pattern — and then every Wednesday changes. */}
      {(e.kind === 'booked' || e.kind === 'open') && s.overridden && (
        <div className="truncate text-[10px] text-hunter/70">
          just this day{s.overrideNote ? ` · ${s.overrideNote}` : ''}
        </div>
      )}
    </div>
  );
}
